import React, { useEffect, useState, useCallback, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  Switch,
  TablePagination,
  TextField,
  Box,
  Alert,
  Snackbar,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  Stack,
  Typography,
  CircularProgress
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import LockResetIcon from '@mui/icons-material/LockReset';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import { User } from '../../types/user';
import { userService } from '../../services/userService';
import { sanitizeEmail } from '../../utils/inputSanitizer';
import { useAuth } from '../../context/AuthContext';
import { EmptyState, PageHeader, StatusChip } from '../ui';

/** Cognito-style status strings mapped onto the shared status palette. */
const statusTone = (status: string) => {
  const normalised = (status || '').toUpperCase();
  if (normalised === 'CONFIRMED') return 'success' as const;
  if (normalised.includes('FORCE_CHANGE') || normalised === 'UNCONFIRMED') return 'warning' as const;
  if (normalised.includes('DISABLED') || normalised.includes('RESET')) return 'error' as const;
  return 'neutral' as const;
};

export const UserList: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [total, setTotal] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [pwTarget, setPwTarget] = useState<string | null>(null);
  const [newPw, setNewPw] = useState('');
  const [savingPw, setSavingPw] = useState(false);
  const [openCreateDialog, setOpenCreateDialog] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', password: '12345678' });
  const [isSearching, setIsSearching] = useState(false);
  const tokensPerPageRef = useRef<string[]>(['']);
  const [creatingUser, setCreatingUser] = useState(false);
  const { hasRole } = useAuth();

  // Check if user can create new users
  const canCreateUser = hasRole('Admin', 'UserManager');

  // Reset tokens when search changes
  useEffect(() => {
    tokensPerPageRef.current = [''];
    setPage(0);
  }, [debouncedSearchTerm]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 1000);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  const fetchUsers = useCallback(async () => {
    try {
      setIsSearching(true);
      const searchTermToUse = debouncedSearchTerm.trim() || '';

      if (searchTermToUse) {
        // When searching, use page-based pagination
        const response = await userService.getUsers(rowsPerPage, page, searchTermToUse, null);
        setUsers(response.users);
        setTotal(response.total);
        // Clear tokensPerPage when searching since we use page-based pagination
        tokensPerPageRef.current = [''];
      } else {
        // When not searching, use token-based pagination
        const response = await userService.getUsers(rowsPerPage, 0, '', tokensPerPageRef.current[page] || null);
        setUsers(response.users);
        setTotal(response.total);
        // Save nextToken for next page
        tokensPerPageRef.current[page + 1] = response.nextToken || '';
      }
    } catch (error) {
      setError('Failed to fetch users');
      console.error('Error fetching users:', error);
    } finally {
      setIsSearching(false);
    }
  }, [rowsPerPage, page, debouncedSearchTerm]);

  // Initial load and when dependencies change
  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    tokensPerPageRef.current = [''];
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleChangePassword = async () => {
    if (!pwTarget) return;
    if (newPw.length < 8 || newPw.length > 200) {
      setError('Mật khẩu phải từ 8 đến 200 ký tự.');
      return;
    }
    setSavingPw(true);
    try {
      const res = await userService.changePassword(pwTarget, newPw);
      if (!res.result) throw new Error(res.message);
      setSuccessMessage(res.message);
      setPwTarget(null);
      setNewPw('');
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSavingPw(false);
    }
  };

  const handleToggleEnabled = async (email: string, currentEnabled: boolean) => {
    try {
      const response = await userService.updateUserStatus(email, !currentEnabled);
      if (response.result) {
        // Chỉ update UI khi backend OK
        setUsers(prevUsers =>
          prevUsers.map(user =>
            user.email === email
              ? { ...user, enable: currentEnabled }
              : user
          )
        );
        setSuccessMessage('User status updated successfully');
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError('Failed to update user status');
      // Không update UI nếu lỗi
    }
  };

  const handleCreateUser = async () => {
    setCreatingUser(true);
    try {
      const response = await userService.createUser(newUser.email, newUser.password);
      if (response.result) {
        setSuccessMessage('User created successfully');
        setOpenCreateDialog(false);
        setNewUser({ email: '', password: '12345678' });
        fetchUsers(); // Refresh the list
      } else {
        setError(response.message);
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Failed to create user');
    } finally {
      setCreatingUser(false);
    }
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccessMessage(null)} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>

      <PageHeader
        eyebrow="Licensing"
        title="Users"
        description="Manager accounts, their sign-in status and whether they are enabled."
        actions={
          canCreateUser ? (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                setNewUser({ email: '', password: '12345678' });
                setOpenCreateDialog(true);
              }}
            >
              Add user
            </Button>
          ) : undefined
        }
      >
        <TextField
          sx={{ width: { xs: '100%', sm: 360 } }}
          placeholder="Search by email"
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchRoundedIcon fontSize="small" sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            ),
            endAdornment: isSearching ? (
              <InputAdornment position="end">
                <CircularProgress size={14} />
              </InputAdornment>
            ) : undefined,
          }}
        />
      </PageHeader>

      <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Email</TableCell>
              <TableCell>Enabled</TableCell>
              <TableCell>Created Date</TableCell>
              <TableCell>Last Modified</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isSearching ? (
              <TableRow>
                <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                  <Stack alignItems="center" spacing={1.5}>
                    <CircularProgress size={22} />
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      Loading users…
                    </Typography>
                  </Stack>
                </TableCell>
              </TableRow>
            ) : users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ p: 0 }}>
                  <EmptyState
                    icon={<GroupOutlinedIcon />}
                    title={debouncedSearchTerm ? 'No matching users' : 'No users yet'}
                    description={
                      debouncedSearchTerm
                        ? `Nothing matches “${debouncedSearchTerm}”. Try a different email fragment.`
                        : 'Accounts you create will be listed here.'
                    }
                  />
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.email} hover>
                  <TableCell sx={{ fontWeight: 500 }}>{user.email}</TableCell>
                  <TableCell>
                    <Switch
                      checked={user.enable}
                      onChange={() => handleToggleEnabled(user.email, !user.enable)}
                    />
                  </TableCell>
                  <TableCell>
                    {user.createDate && !isNaN(new Date(Number(user.createDate)).getTime())
                      ? new Date(Number(user.createDate)).toLocaleDateString()
                      : ''}
                  </TableCell>
                  <TableCell>
                    {user.lastModified && !isNaN(new Date(Number(user.lastModified)).getTime())
                      ? new Date(Number(user.lastModified)).toLocaleString()
                      : ''}
                  </TableCell>
                  <TableCell>
                    <StatusChip label={user.status || 'UNKNOWN'} tone={statusTone(user.status)} />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<LockResetIcon />}
                      onClick={() => { setPwTarget(user.email); setNewPw(''); }}
                    >
                      Đổi mật khẩu
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100, 200]}
      />
      </Card>

      <Dialog open={!!pwTarget} onClose={() => setPwTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Đổi mật khẩu</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Đặt mật khẩu mới cho <b>{pwTarget}</b>. Mọi phiên đang đăng nhập của tài khoản này sẽ bị thu hồi.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            type="password"
            label="Mật khẩu mới"
            placeholder="Tối thiểu 8 ký tự"
            value={newPw}
            onChange={(e) => setNewPw(e.target.value)}
            disabled={savingPw}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPwTarget(null)} disabled={savingPw}>Huỷ</Button>
          <Button variant="contained" onClick={() => void handleChangePassword()} disabled={savingPw}>
            {savingPw ? 'Đang lưu…' : 'Đổi mật khẩu'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openCreateDialog} onClose={() => setOpenCreateDialog(false)}>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Email"
              variant="outlined"
              value={newUser.email}
              onChange={(e) => setNewUser(prev => ({ ...prev, email: sanitizeEmail(e.target.value) }))}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label="Password"
              type="password"
              variant="outlined"
              value={newUser.password}
              onChange={(e) => setNewUser(prev => ({ ...prev, password: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenCreateDialog(false)}>Cancel</Button>
          <Button
            onClick={handleCreateUser}
            variant="contained"
            color="primary"
            disabled={!newUser.email || !newUser.password || creatingUser}
          >
            {creatingUser ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};